// src/lib/pm-appointment-status.ts
//
// APPOINTMENT STATUS COLOUR + NAMING, TAKEN FROM PM (Andrew, 2026-08-28: "the appointment colors
// and naming please take them from pm react for the calendar, today screen, encounter patient card").
//
// WHY THIS MODULE EXISTS. PA had TWO status systems:
//   · the CALENDAR renders `components/atoms/status-badge.tsx` — a byte-identical copy of PM's
//     `StatusBadge`, so the calendar already matched PM and is untouched by this file.
//   · the TODAY screen and the ENCOUNTER patient card resolved colour through
//     `lib/appointment-status.ts` → the DS canonical map, which PM never adopted (PM uses
//     StatusBadge on every surface including its own encounter, `pages/clinical/FrameEncounter.tsx`).
// Those two disagreed. This module is the bridge: PM's buckets and labels, expressed as the DS
// `StatusChip` semantic that the Today card and the encounter pill actually accept.
//
// ON SAMER'S PROD-1588 REJECT (Booked = GRAY). Andrew authorised reversing it — he was shown that
// PM's `booked` is the same info-blue the reject overturned, and chose full PM parity. It then turned
// out the DS cannot render PM's blue on these surfaces at all (see SEMANTIC_BY_BUCKET below), so
// Booked stays gray and the reject stands UNREVERSED. No amendment to PROD-1588 is needed for it.
// If the DS `tone` passthrough later lands, flipping Booked to blue DOES reverse that reject and
// should carry a real ticket amendment — a verbal instruction plus a code comment is the
// "self-signed permit" the standing rules warn against.
//
// SOURCE OF TRUTH — PM `src/components/atoms/status-badge.tsx`:
//   arrived     bg-brand-primary text-text-inverse   confirmed  bg-status-success text-text-inverse
//   booked      bg-blue-100 text-brand-primary       noShow     bg-red-100 text-red-700
//   cancelled   bg-grey-100 text-text-muted          waitlisted bg-yellow-100 text-yellow-700
//   checkedIn   bg-blue-200 text-brand-depth         fulfilled  bg-green-100 text-green-700
import {
  appointmentStatusFromCode,
  type AppointmentStatusType,
  type StatusChipProps,
} from '@scripsteam/scrips-signal-ds';

type Semantic = NonNullable<StatusChipProps['semantic']>;

/**
 * PM bucket → the nearest DS `StatusChip` semantic.
 *
 * ⛔ PM'S BLUE IS BLOCKED ON THESE TWO SURFACES — measured, not assumed.
 *
 * PM's blue statuses are TINTS: `booked` = `bg-blue-100` (#E6F1FE) + brand-primary text,
 * `checkedIn` = `bg-blue-200` (#B3D6FD) + brand-depth text. The DS equivalent is the chip's
 * `tone="soft"` (`--color-status-info-bg` IS #E6F1FE — an exact match for PM's booked background).
 *
 * But neither consumer exposes `tone`: `WorklistAppointmentCardProps` takes only `statusSemantic`,
 * and `SessionStatusControlProps` only `semantic`. Both therefore render `tone="solid"`, where
 * `info` = `--color-status-info` = **#5A6B75 — a slate GRAY, byte-identical to `--color-status-
 * neutral`**. Verified on dev 2026-08-28: mapping CheckedIn to `info` painted the encounter pill
 * rgb(90,107,117), i.e. gray — not PM's blue, and worse than the green it replaced.
 *
 * So `booked` and `checkedIn` KEEP their previous DS values below. Shipping `info` would have been
 * a fake port: the right colour name resolving to the wrong colour. Per the DS-first rule, a surface
 * whose DS capability isn't shipped is BLOCKED and flagged — not faked. Unblocking it means a PR to
 * `Scripsteam/scrips-signal-ds` adding a `tone` passthrough on those two components (or a distinct
 * blue solid semantic), after which the two lines marked BLOCKED become `info` + soft.
 *
 * CONSEQUENCE WORTH NOTING: because PM's blue is unreachable, Samer's PROD-1588 reject (Booked =
 * GRAY) is NOT in fact reversed — Booked stays gray. Andrew authorised reversing it, but the DS
 * makes the point moot, so no amendment to that ticket is needed for Booked. Everything else below
 * IS PM's.
 */
const SEMANTIC_BY_BUCKET: Record<AppointmentStatusType, Semantic> = {
  arrived: 'primary', // PM: solid brand primary — exact match (both solid #005FD4 + white)
  confirmed: 'success', // PM: bg-status-success
  booked: 'neutral', // BLOCKED — PM is blue-100; `info` solid renders #5A6B75 gray. Unchanged.
  noShow: 'error', // PM: red-100 / red-700 — same family
  cancelled: 'neutral', // PM: grey-100 — NOT red; the DS canonical map had this as `error`
  waitlisted: 'warning', // PM: yellow-100 — the DS map had Waitlist/Pending/Proposed as neutral/primary
  checkedIn: 'success', // BLOCKED — PM is blue-200; `info` solid is gray, which reads worse than green
  fulfilled: 'success', // PM: green-100 / green-700 — same family
};

/**
 * The two buckets still awaiting a DS `tone` passthrough before they can take PM's blue. Exported so
 * the block is auditable rather than a comment someone has to notice — the same pattern the DS uses
 * for `SESSION_STATUS_OPTIONS_STOPPED`.
 */
export const PM_COLOUR_BLOCKED: { bucket: AppointmentStatusType; pm: string; reason: string }[] = [
  {
    bucket: 'booked',
    pm: 'bg-blue-100 (--color-blue-100) + brand-primary text',
    reason:
      'DS StatusChip solid `info` = --color-status-info = --color-grey-700 (gray, same as neutral); the ' +
      'blue lives in the soft tone and neither WorklistAppointmentCard nor SessionStatusControl ' +
      'exposes `tone`.',
  },
  {
    bucket: 'checkedIn',
    pm: 'bg-blue-200 (--color-blue-200) + brand-depth text',
    reason:
      'Same missing `tone` passthrough. PM also distinguishes booked/checkedIn by SHADE, which the ' +
      'single DS blue could not express even with soft tone.',
  },
];

/**
 * PM `STATUS_LABELS`, mirrored. Kept here rather than exported from `status-badge.tsx` so that file
 * stays BYTE-IDENTICAL to PM's copy — that identity is what lets a `diff` prove calendar parity, and
 * it is worth more than avoiding this small duplication. If PM's labels change, change both.
 */
const LABEL_BY_BUCKET: Record<AppointmentStatusType, string> = {
  arrived: 'Arrived',
  confirmed: 'Confirmed',
  booked: 'Booked',
  noShow: 'No Show',
  cancelled: 'Cancelled',
  waitlisted: 'Waitlisted',
  checkedIn: 'Checked In',
  fulfilled: 'Completed', // the wire code stays Fulfilled; users see "Completed"
};

/**
 * `InSession` → `CheckedIn` before PM bucketing.
 *
 * The in-encounter dropdown offers an `InSession` option (SESSION_STATUS_OPTIONS — Samer's
 * decision-strip ruling 2026-07-22, PROD-1588, the Figma six-list) whose value is deliberately NOT a
 * backend enum member; the DS canonical ALIAS folds `insession` onto `CheckedIn`. PM has no such
 * concept, so `appointmentStatusFromCode('InSession')` would fall through to its `booked` default
 * and paint an in-session patient as merely booked. Fold it first.
 */
function normaliseForPm(raw: string | null | undefined): string | null | undefined {
  const c = (raw ?? '').toLowerCase().replace(/[\s_-]/g, '');
  if (c === 'insession' || c === 'inprogress') return 'CheckedIn';
  return raw;
}

/** PM's bucket for a wire code or display string. */
export function pmStatusBucket(raw: string | null | undefined): AppointmentStatusType {
  return appointmentStatusFromCode(normaliseForPm(raw));
}

/** The DS chip semantic PM would paint this status. */
export function pmStatusSemantic(raw: string | null | undefined): Semantic {
  return SEMANTIC_BY_BUCKET[pmStatusBucket(raw)];
}

/**
 * The label PM would show — its `StatusBadge.displayLabel` rule exactly:
 *
 *     status === 'fulfilled' ? 'Completed' : (label || STATUS_LABELS[status])
 *
 * so a server `display` wins when present, EXCEPT for fulfilled, which is forced to "Completed"
 * because the API display for it is sometimes the raw "Fulfilled". `||` not `??` is deliberate and
 * carried over from PM: an empty-string display must fall back to the canonical label, or the pill
 * renders blank.
 */
export function pmStatusLabel(
  raw: string | null | undefined,
  serverDisplay?: string | null,
): string {
  const bucket = pmStatusBucket(raw);
  if (bucket === 'fulfilled') return 'Completed';
  return serverDisplay || LABEL_BY_BUCKET[bucket];
}

/**
 * The person card's status pill in the pre-encounter rail (Antonin, 16 Oct: "not sure why the left panel checked is a green
 * chip with blue in it, violation of colour. should be soft blue"). The green is THIS file's `checkedIn: 'success'` (a
 * workaround for the missing DS `tone`), which the bridge draws as the soft green chip. For the rail pill only, the
 * checked-in bucket is `primary`, which the bridge draws as the one soft blue chip (the same as Arrived). The started
 * note's chip (D-49: "the live CheckedIn state the ONE solid chip") and the other places keep `pmStatusSemantic`, whose tests
 * encode that desk ruling; moving them is a design decision, not done here.
 */
export function pmRailStatusSemantic(raw: string | null | undefined): Semantic {
  return pmStatusBucket(raw) === 'checkedIn' ? 'primary' : pmStatusSemantic(raw);
}
